import { ApiError } from "@nabble/sdk";
import { Alert, Button, Field, Modal, Select, useToast } from "@nabble/ui";
import { type FormEvent, useState } from "react";
import type { Organization } from "../../../api/adminOrganizations";
import type { Role } from "../../../api/adminRoles";
import { createUser } from "../../../api/adminUsers";
import { useAuth } from "../../../auth/AuthContext";
import { NewPersonFields } from "../../../components/NewPersonFields";
import { RolePicker } from "../../../components/RolePicker";
import { errorMessage } from "../../../errors";
import { type NewPerson, type SubmitFailure, useNewPersonForm } from "../../../hooks/useNewPersonForm";
import { DEFAULT_ROLE_IDS } from "../../../model/organizationRoles";
import { SuiteAccessFields } from "./SuiteAccessFields";

interface CreateUserDialogProps {
  roles: Role[];
  organizations: Organization[];
  onClose: () => void;
  /** `link` is the invitation to share by hand when the email couldn't be sent. */
  onCreated: (email: string, link: string | null) => void;
}

function explainFailure(err: unknown): SubmitFailure {
  if (err instanceof ApiError && err.code === "email_taken") return { field: "email", text: "That email is already in use." };
  return { field: "form", text: errorMessage(err, "We couldn't create the user.") };
}

export function CreateUserDialog({ roles, organizations, onClose, onCreated }: CreateUserDialogProps): React.JSX.Element {
  const { show } = useToast();
  const { user: me } = useAuth();
  const form = useNewPersonForm();
  const [organizationId, setOrganizationId] = useState<string>(me?.organization?.id ?? organizations[0]?.id ?? "");
  const [roleIds, setRoleIds] = useState<string[]>([...DEFAULT_ROLE_IDS]);
  const [suiteAdmin, setSuiteAdmin] = useState(false);

  async function send(person: NewPerson): Promise<void> {
    const result = await createUser({ ...person, organizationId, roleIds, suiteAdmin });
    show({
      title: result.emailSent ? "Invitation sent" : "User created",
      text: result.emailSent ? `We sent ${person.email} an email to create their password.` : undefined,
      tone: "success",
    });
    onCreated(person.email, result.emailSent ? null : result.link);
  }

  const submit = (e?: FormEvent): Promise<void> => form.submit(e, send, explainFailure);

  return (
    <Modal
      open
      icon="userPlus"
      title="Invite user"
      description="They'll get an email with a link to create their password and set up their second factor."
      onClose={onClose}
      actions={
        <>
          <Button label="Cancel" variant="secondary" onClick={onClose} />
          <Button label="Send invitation" icon="send" loading={form.loading} disabled={!form.filled || roleIds.length === 0} onClick={() => void submit()} />
        </>
      }
    >
      <form className="nb-stack" onSubmit={(e) => void submit(e)} noValidate>
        <NewPersonFields form={form} />
        <Field label="Organization">
          <Select value={organizationId} onValueChange={setOrganizationId} options={organizations.map((o) => ({ value: o.id, label: o.name }))} />
        </Field>
        <RolePicker roles={roles} value={roleIds} onChange={setRoleIds} label="Roles in the organization" showLabel />
        <SuiteAccessFields suiteAdmin={suiteAdmin} onChange={setSuiteAdmin} />
        {form.error ? <Alert tone="danger">{form.error}</Alert> : null}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
